import { useEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import type { ProductType } from '@/features/product/types';
import { CountryPathwayStep } from './CountryPathwayStep';
import {
  DRUG_FINDER_FOR,
  FINDER_QUALIFYING_LABEL,
  NOT_SURE,
  showsInFinder,
} from '@/features/product/submission-pathways';
import { DRUG_FINDER_HERO } from '@/features/predicate/components/drug/copy';
import {
  useEuIdentifiers,
  useProductForEdit,
  useSetEuIdentifiers,
  useSetSubmissionPathways,
  useSubmissionPathways,
} from '@/features/product/hooks';
import type { EuIdentifiers } from '@/features/product/api';
import {
  EMPTY_EU_IDENTIFIERS,
  euIdentifiersChanged,
  keptEuIdentifiers,
  showsEuIdentifiers,
} from '@/features/product/eu-identifiers';
import { EuIdentifiersFields } from './EuIdentifiersFields';

function isProductType(v: unknown): v is ProductType {
  return (
    v === 'device' || v === 'drug' || v === 'biologic' || v === 'combination'
  );
}

/**
 * Post-creation editor for the Step-2 country / regulatory-classification map.
 * Reuses the same `CountryPathwayStep` control as product creation, seeded from
 * the stored map and autosaving each change through the submission-pathways
 * endpoint (which also refreshes finder eligibility).
 */
export function CountryClassificationCard({ productId }: { productId: number }) {
  const snapshot = useProductForEdit(productId);
  const pathwaysQ = useSubmissionPathways(productId);
  const save = useSetSubmissionPathways(productId);

  const [value, setValue] = useState<Record<string, string>>({});
  const seeded = useRef(false);

  // Seed local state once the stored map arrives; don't clobber later edits.
  useEffect(() => {
    if (!seeded.current && pathwaysQ.data) {
      setValue(pathwaysQ.data.pathways);
      seeded.current = true;
    }
  }, [pathwaysQ.data]);

  const rawType = snapshot.data?.product_type;
  const productType = isProductType(rawType) ? rawType : null;
  const drugFinder = productType ? DRUG_FINDER_FOR[productType] : undefined;
  // Held here, not in the box: the box sits in the EU tab and is rebuilt when countries are added or
  // removed (no tabs ↔ tabs). Rebuilt with its own state, it refilled from a not-yet-updated server
  // copy and saved that old value back (QA audit e49590ae). This card is never rebuilt.
  const euEditor = useEuIdentifiersEditor(productId, showsEuIdentifiers(productType, value));

  const onChange = (next: Record<string, string>) => {
    setValue(next);
    save.mutate(next);
  };

  return (
    <section className="bg-white border border-line rounded-2xl p-6 shadow-card">
      <div className="mb-1 flex items-start justify-between gap-4">
        <h2 className="text-navy text-[18px] font-bold m-0">
          Countries &amp; Regulatory Classification
        </h2>
        {save.isPending ? (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-muted">
            <Loader2 className="size-3.5 animate-spin" /> Saving&hellip;
          </span>
        ) : save.isError ? (
          <span className="text-[12px] text-state-error-text">
            Couldn&rsquo;t save &mdash; try again
          </span>
        ) : save.isSuccess ? (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-state-success-text">
            <Check className="size-3.5" /> Saved
          </span>
        ) : null}
      </div>
      <p className="text-muted text-[13px] m-0 mb-3">
        Where you intend to submit and each market&rsquo;s regulatory
        classification. This drives which products appear in the agent tables.
        Changes save automatically.
      </p>
      {productType === 'device' && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5 text-[12.5px]">
          <span className="text-muted">Existing Products:</span>
          {showsInFinder(productType, value) ? (
            <span className="inline-flex items-center gap-1 font-semibold text-state-success-text">
              <Check className="size-3.5" /> shows this product
            </span>
          ) : value['US'] === NOT_SURE || value['EU'] === NOT_SURE ? (
            <span className="text-muted">hidden until you classify it</span>
          ) : (
            <span className="text-muted">
              hidden &mdash; needs{' '}
              <span className="font-semibold text-navy">
                {FINDER_QUALIFYING_LABEL['US']}
              </span>{' '}
              or{' '}
              <span className="font-semibold text-navy">
                {FINDER_QUALIFYING_LABEL['EU']}
              </span>
            </span>
          )}
        </div>
      )}
      {drugFinder && productType && (
        // The drug / biologic finder's twin of the device line (US-only).
        <div className="mb-4 flex flex-wrap items-center gap-1.5 text-[12.5px]">
          <span className="text-muted">{DRUG_FINDER_HERO[drugFinder.kind].title}:</span>
          {showsInFinder(productType, value) ? (
            <span className="inline-flex items-center gap-1 font-semibold text-state-success-text">
              <Check className="size-3.5" /> shows this product
            </span>
          ) : value['US'] === NOT_SURE ? (
            <span className="text-muted">hidden until you classify it</span>
          ) : (
            <span className="text-muted">
              hidden &mdash; needs{' '}
              <span className="font-semibold text-navy">{drugFinder.label}</span>
            </span>
          )}
        </div>
      )}
      {pathwaysQ.isLoading || snapshot.isLoading ? (
        <p className="text-muted text-[13px]">Loading&hellip;</p>
      ) : pathwaysQ.isError ? (
        <p className="text-state-error-text text-[13px]">
          Couldn&rsquo;t load the current selection.
        </p>
      ) : (
        <>
          <CountryPathwayStep
            productType={productType}
            value={value}
            onChange={onChange}
            // Inside the European Union tab only (Marc 2026-10-22: it showed on the US tab too).
            renderCountryExtra={(c) =>
              c === 'EU' && showsEuIdentifiers(productType, value) ? (
                <EuIdentifiersEditor editor={euEditor} />
              ) : null
            }
          />
        </>
      )}
    </section>
  );
}

/** The device's Basic UDI-DI / UDI-DI(s), seeded from the product and saved when a field is left. */
function useEuIdentifiersEditor(productId: number, enabled: boolean) {
  const idsQ = useEuIdentifiers(productId, enabled);
  const save = useSetEuIdentifiers(productId);
  const [ids, setIds] = useState<EuIdentifiers>(EMPTY_EU_IDENTIFIERS);
  const seeded = useRef(false);
  // What was last sent — compared against instead of the server copy, which lags while a save is in
  // flight (leave a field, then click Remove at once: the removal looked like "no change").
  const lastSent = useRef<EuIdentifiers | null>(null);
  useEffect(() => {
    if (!seeded.current && idsQ.data) {
      setIds(idsQ.data);
      seeded.current = true;
    }
  }, [idsQ.data]);
  const commit = (next: EuIdentifiers) => {
    const kept = keptEuIdentifiers(next);
    // After a failed save (e.g. 429, too many saves) what was "last sent" never landed: compare with
    // what is on file, so leaving the field again retries it.
    const sent = save.isError ? null : lastSent.current;
    if (euIdentifiersChanged(kept, sent ?? idsQ.data ?? EMPTY_EU_IDENTIFIERS)) {
      lastSent.current = kept;
      save.mutate(kept);
    }
  };
  return { idsQ, saveFailed: save.isError, ids, setIds, commit };
}

function EuIdentifiersEditor({ editor }: { editor: ReturnType<typeof useEuIdentifiersEditor> }) {
  const { idsQ, saveFailed, ids, setIds, commit } = editor;
  // A failed load says so (code review 2026-10-22: the box vanished with no message). Never an empty
  // editor: saving it would overwrite what is on file.
  if (idsQ.isError) {
    return (
      <p className="mt-5 text-state-error-text text-[12.5px]">
        Couldn&rsquo;t load the EU device identifiers. Refresh the page to try again.
      </p>
    );
  }
  // Wait for the saved identifiers: never an empty editor that a save would write over what is on file.
  if (!idsQ.data) return null;
  return (
    <div className="mt-5">
      {/* Opens expanded, every field in view, saved or not (Marc 2026-10-22). */}
      <EuIdentifiersFields value={ids} onChange={setIds} onCommit={commit} defaultOpen />
      {saveFailed && (
        <p className="text-state-error-text text-[12px] mt-1">Couldn&rsquo;t save the identifiers. Try again in a minute.</p>
      )}
    </div>
  );
}
